"use client";
import React, { useState, useEffect, useRef } from "react";
import GoogleMaterialIcon from "@/components/ui/GoogleMaterialIcon";
import {
  AlertIcon,
  CheckCircleIcon,
  ErrorIcon,
  InfoIcon,
} from "@/icons";

interface NotificationProps {
  variant: "success" | "info" | "warning" | "error"; // Notification type
  title: string; // Title text
  description?: string; // Optional description
  hideDuration?: number; // Time in milliseconds to hide the notification (default: 5000ms)
  onClose?: () => void; // Callback when notification is closed
}

const Notification: React.FC<NotificationProps> = ({
  variant,
  title,
  description,
  hideDuration = 12000, // Default hide duration: 12 seconds
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100); // Progress starts at 100% and counts down
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Styling configuration for each alert type
  const variantStyles = {
    success: {
      borderColor: "border-success-500",
      iconBg: "bg-success-50 text-success-500",
      progressBg: "bg-success-500",
      icon: <CheckCircleIcon />,
    },
    info: {
      borderColor: "border-blue-light-500",
      iconBg: "bg-blue-light-50 text-blue-light-500",
      progressBg: "bg-blue-light-500",
      icon: <InfoIcon />,
    },
    warning: {
      borderColor: "border-warning-500",
      iconBg: "bg-warning-50 text-warning-500",
      progressBg: "bg-warning-500",
      icon: <AlertIcon />,
    },
    error: {
      borderColor: "border-error-500",
      iconBg: "bg-error-50 text-error-500",
      progressBg: "bg-error-500",
      icon: <ErrorIcon />,
    },
  };

  const { borderColor, iconBg, progressBg, icon } = variantStyles[variant];

  // Countdown progress bar effect
  useEffect(() => {
    const updateInterval = 50; // Update every 50ms for smooth animation
    const decrementAmount = (updateInterval / hideDuration) * 100;

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - decrementAmount;
        if (newProgress <= 0) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          return 0;
        }
        return newProgress;
      });
    }, updateInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [hideDuration]);

  const handleClose = () => {
    // Stop the countdown
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    // Hide the notification
    setIsVisible(false);
    
    // Call the onClose callback if provided
    if (onClose) {
      onClose();
    }
  };

  if (!isVisible) return null; // Don't render anything if not visible

  return (
    // An error interrupts; everything else rides the container's polite region.
    // Per-toast rather than on the container, because changing a live region's
    // role as its content arrives is what makes announcements get dropped.
    <div
      role={variant === 'error' ? 'alert' : undefined}
      className="relative flex flex-col gap-3 w-full sm:max-w-[340px] rounded-md p-3 shadow-theme-sm bg-white dark:bg-[#1E2634] overflow-hidden"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-lg ${iconBg}`}
          >
            {icon}
          </div>

          {/* Title and Description */}
          <div>
            <h4 className="text-sm text-gray-800 sm:text-base dark:text-white/90">
              {title}
            </h4>
            {description && (
              // whitespace-pre-line lets callers use \n to break a multi-item
              // message onto separate lines (e.g. a bulleted list of items
              // that didn't save) instead of collapsing into a single blob.
              <p className="mt-1 text-xs text-gray-600 sm:text-sm dark:text-white/70 whitespace-pre-line">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors"
          aria-label="Close notification"
        >
          <GoogleMaterialIcon name="close" size="sm" />
        </button>
      </div>

      {/* Countdown Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700">
        <div
          className={`h-full transition-all duration-50 ease-linear ${progressBg}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default Notification;
